import { useTranslation } from "react-i18next";
import { Table, Toast } from "@/shared/ui";
import { toNumber } from "@/shared/utils";
import { formatMoneyKzt } from "@/features/accounting/shared";
import type { TrialBalanceResponse } from "../../../types";

interface Props {
  data?: TrialBalanceResponse;
  isLoading?: boolean;
  error?: unknown;
}

export default function TrialBalanceSection({ data, isLoading, error }: Props) {
  const { t } = useTranslation("ReportsPage");

  if (isLoading) {
    return null;
  }

  if (error || !data) {
    return (
      <div className="py-5 px-6 radius-3xl surface-base-fill mb-4">
        <div className="flex flex-col gap-0.5 mb-5">
          <h3 className="text-body-bold-lg content-base-primary">{t("trialBalance.title")}</h3>
          <p className="text-body-regular-md content-action-neutral">{t("trialBalance.subtitle")}</p>
        </div>
        <div className="text-body-regular-md content-action-neutral">
          {error ? t("messages.error") : t("messages.noResults")}
        </div>
      </div>
    );
  }

  const totalOpeningDebit = data.rows.reduce(function sumOpeningDebit(sum, row) {
    return sum + toNumber(row.opening_debit);
  }, 0);
  const totalOpeningCredit = data.rows.reduce(function sumOpeningCredit(sum, row) {
    return sum + toNumber(row.opening_credit);
  }, 0);
  const totalDebitTurnover = data.rows.reduce(function sumDebitTurnover(sum, row) {
    return sum + toNumber(row.debit_turnover);
  }, 0);
  const totalCreditTurnover = data.rows.reduce(function sumCreditTurnover(sum, row) {
    return sum + toNumber(row.credit_turnover);
  }, 0);
  const totalClosingDebit = data.rows.reduce(function sumClosingDebit(sum, row) {
    return sum + toNumber(row.closing_debit);
  }, 0);
  const totalClosingCredit = data.rows.reduce(function sumClosingCredit(sum, row) {
    return sum + toNumber(row.closing_credit);
  }, 0);

  return (
    <div className="py-5 px-6 radius-3xl surface-base-fill mb-4">
      <div className="flex justify-between items-center gap-5 mb-5">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-body-bold-lg content-base-primary">{t("trialBalance.title")}</h3>
          <p className="text-body-regular-md content-action-neutral">{t("trialBalance.subtitle")}</p>
        </div>
        {data.is_balanced ? (
          <Toast color="positive" text={t("trialBalance.balanced")} closable={false} autoClose={false} />
        ) : (
          <Toast color="negative" text={t("trialBalance.unbalanced")} closable={false} autoClose={false} />
        )}
      </div>

      <div className="overflow-x-auto">
        <Table.Table>
          <Table.Header>
            <tr>
              <Table.HeadCell>{t("trialBalance.table.code")}</Table.HeadCell>
              <Table.HeadCell>{t("trialBalance.table.name")}</Table.HeadCell>
              <Table.HeadCell align="right">{t("trialBalance.table.openingDebit")}</Table.HeadCell>
              <Table.HeadCell align="right">{t("trialBalance.table.openingCredit")}</Table.HeadCell>
              <Table.HeadCell align="right">{t("trialBalance.table.debitTurnover")}</Table.HeadCell>
              <Table.HeadCell align="right">{t("trialBalance.table.creditTurnover")}</Table.HeadCell>
              <Table.HeadCell align="right">{t("trialBalance.table.closingDebit")}</Table.HeadCell>
              <Table.HeadCell align="right">{t("trialBalance.table.closingCredit")}</Table.HeadCell>
            </tr>
          </Table.Header>
          <Table.Body>
            {data.rows.map(function renderRow(row) {
              return (
                <Table.Row key={row.code}>
                  <Table.Cell>{row.code}</Table.Cell>
                  <Table.Cell>{row.name}</Table.Cell>
                  <Table.Cell align="right">{formatMoneyKzt(row.opening_debit)}</Table.Cell>
                  <Table.Cell align="right">{formatMoneyKzt(row.opening_credit)}</Table.Cell>
                  <Table.Cell align="right">{formatMoneyKzt(row.debit_turnover)}</Table.Cell>
                  <Table.Cell align="right">{formatMoneyKzt(row.credit_turnover)}</Table.Cell>
                  <Table.Cell align="right">{formatMoneyKzt(row.closing_debit)}</Table.Cell>
                  <Table.Cell align="right">{formatMoneyKzt(row.closing_credit)}</Table.Cell>
                </Table.Row>
              );
            })}
            <Table.Row>
              <Table.Cell isBold>{t("trialBalance.table.total")}</Table.Cell>
              <Table.Cell isBold>—</Table.Cell>
              <Table.Cell align="right" isBold>
                {formatMoneyKzt(totalOpeningDebit.toString())}
              </Table.Cell>
              <Table.Cell align="right" isBold>
                {formatMoneyKzt(totalOpeningCredit.toString())}
              </Table.Cell>
              <Table.Cell align="right" isBold>
                {formatMoneyKzt(totalDebitTurnover.toString())}
              </Table.Cell>
              <Table.Cell align="right" isBold>
                {formatMoneyKzt(totalCreditTurnover.toString())}
              </Table.Cell>
              <Table.Cell align="right" isBold>
                {formatMoneyKzt(totalClosingDebit.toString())}
              </Table.Cell>
              <Table.Cell align="right" isBold>
                {formatMoneyKzt(totalClosingCredit.toString())}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Table>
      </div>
    </div>
  );
}
